import type * as React from 'react';
import { useId } from 'react';

import { Badge, type BadgeColor, type BadgeSize } from '../Badge';
import { BadgePopover } from '../BadgePopover';

export type StatusBadgeTooltip = {
  title: React.ReactNode;
  description: React.ReactNode;
};

export type StatusBadgeProps = Omit<React.ComponentProps<'span'>, 'color'> & {
  color?: BadgeColor;
  size?: BadgeSize;
  tooltip?: StatusBadgeTooltip;
};

/**
 * Status badge that optionally opens a title and description card. With a card, the badge is a button:
 * hover opens the card, and click, Enter or Space open it with focus inside so links in it are reachable.
 * Pass `aria-label` to name a badge repeated per row; it must contain the visible text.
 */
function StatusBadge({ tooltip, className, children, ...props }: StatusBadgeProps) {
  if (!tooltip) {
    return (
      <Badge className={className} {...props}>
        {children}
      </Badge>
    );
  }

  return (
    <StatusBadgeWithCard tooltip={tooltip} className={className} {...props}>
      {children}
    </StatusBadgeWithCard>
  );
}

function StatusBadgeWithCard({
  tooltip,
  className,
  children,
  ...props
}: StatusBadgeProps & { tooltip: StatusBadgeTooltip }) {
  const titleId = useId();
  const descriptionId = useId();

  return (
    <BadgePopover
      cardLabelledBy={titleId}
      cardDescribedBy={descriptionId}
      content={
        <div className="flex w-72 flex-col gap-2 rounded-sm bg-white p-4 text-grey-90 shadow-lg">
          <div id={titleId} className="text-sm font-semibold">
            {tooltip.title}
          </div>
          <div id={descriptionId} className="text-xs">
            {tooltip.description}
          </div>
        </div>
      }
    >
      <Badge asChild className={className} {...props}>
        {/* Restores Badge's data-slot, which the popover trigger's own data-slot replaces. */}
        <button type="button" data-slot="badge">
          {children}
        </button>
      </Badge>
    </BadgePopover>
  );
}

export { StatusBadge };
